import type { Action, BoundActions } from 'react-sweet-state';
import {
	createHook,
	createStore,
	createContainer,
	createStateHook,
	createActionsHook,
} from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';
import { doc, p } from '@atlaskit/adf-utils/builders';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension';

import type { AiIssueCreateState, ContentType } from '@atlassian/ai-issue-create';

import type { InsertStorageFragmentRequestPayload } from '../__types__/InsertStorageFragmentRequestPayload';
import type { InsertStorageColumnTableRequestPayload } from '../__types__/InsertStorageColumnTableRequestPayload';
import type { JiraProjectSelectionPayload } from '../__types__/JiraProjectSelectionPayload';
import type { InsertMultipleStorageFragmentsRequestPayload } from '../__types__/InsertMultipleStorageFragmentsRequestPayload';

export type IssueCreateSidePanelContextType = {
	contentId?: string;
	contentType?: ContentType;
	summary?: string;
	descriptionAdf?: DocNode;
	highlightedText?: DocNode;
	selectedTable?: HTMLTableElement;
	selectedActionItems?: HTMLElement;
	insertStorageFragmentRequestPayload?: InsertStorageFragmentRequestPayload;
	insertStorageColumnTableRequestPayload?: InsertStorageColumnTableRequestPayload;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	firstAttempt: boolean;
	totalRetries: number;
	currentSiteSelection?: string;
	currentProjectSelection?: JiraProjectSelectionPayload;
	aiIssueCreationEnablement?: AiIssueCreationEnablementType;
	isCreateManuallyPressed: boolean;
	isGicIframeReady: boolean;
	pageMode?: PageMode;
	hasEditPermission: boolean;
	editorAPI: PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined;
};

export type AiIssueCreationEnablementType = {
	isEnabled: boolean;
	enablement?: string;
};

type AiAnalyticsContextType = {
	aiResponseLength: number;
	pageContextLength: number;
	highlightedTextLength: number;
};

export enum PageMode {
	VIEW = 'view', // Regular Page View Mode
	EDIT = 'edit', // Regular Page Edit Mode
	LIVE_VIEW = 'live-view', // Live Page View Mode
	LIVE_EDIT = 'live-edit', // Live Page Edit Mode
}

type State = {
	contentId: string;
	contentType: ContentType;
	summary: string;
	descriptionAdf: DocNode;
	highlightedText: DocNode;
	selectedTable: HTMLTableElement | undefined;
	selectedActionItems: HTMLElement | undefined;
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined;
	insertMultipleStorageFragmentsRequestPayload:
		| InsertMultipleStorageFragmentsRequestPayload
		| undefined;
	insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload | undefined;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	firstAttempt: boolean;
	totalRetries: number;
	aiAnalyticsContext: AiAnalyticsContextType;
	currentSiteSelection: string;
	currentProjectSelection: JiraProjectSelectionPayload | undefined;
	aiIssueCreationEnablement: AiIssueCreationEnablementType | undefined;
	isCreateManuallyPressed: boolean;
	isGicIframeReady: boolean;
	pageMode: PageMode | undefined;
	hasEditPermission: boolean;
	isCreatedFromActionItems: boolean;
	editorAPI: PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined;
};

export type IssueCreateSidePanelActions = BoundActions<State, typeof issueCreateSidePanelActions>;

const initialIssueCreateSidePanelState: State = {
	contentId: '',
	contentType: 'page',
	summary: '',
	descriptionAdf: doc(p('')),
	highlightedText: doc(p('')),
	selectedTable: undefined,
	selectedActionItems: undefined,
	insertStorageFragmentRequestPayload: undefined,
	insertMultipleStorageFragmentsRequestPayload: undefined,
	insertStorageColumnTableRequestPayload: undefined,
	aiState: 'idle',
	isCreatedWithAi: false,
	firstAttempt: true,
	totalRetries: 0,
	aiAnalyticsContext: { aiResponseLength: 0, pageContextLength: 0, highlightedTextLength: 0 },
	currentSiteSelection: '',
	currentProjectSelection: undefined,
	aiIssueCreationEnablement: { isEnabled: false },
	isCreateManuallyPressed: false,
	isGicIframeReady: false,
	pageMode: undefined,
	hasEditPermission: false,
	isCreatedFromActionItems: false,
	editorAPI: undefined,
};

const issueCreateSidePanelActions = {
	setContentId:
		(contentId: string): Action<State> =>
		({ setState }) => {
			setState({ contentId });
		},
	setContentType:
		(contentType: State['contentType']): Action<State> =>
		({ setState }) => {
			setState({ contentType });
		},
	setSummary:
		(summary: string): Action<State> =>
		({ setState }) => {
			setState({ summary });
		},
	setDescriptionAdf:
		(descriptionAdf: DocNode): Action<State> =>
		({ setState }) => {
			setState({ descriptionAdf });
		},
	setHighlightedText:
		(highlightedText: State['highlightedText']): Action<State> =>
		({ setState }) => {
			setState({ highlightedText });
		},
	setSelectedTable:
		(selectedTable: State['selectedTable']): Action<State> =>
		({ setState }) => {
			setState({ selectedTable });
		},
	setSelectedActionItems:
		(selectedActionItems: State['selectedActionItems']): Action<State> =>
		({ setState }) => {
			setState({ selectedActionItems });
		},
	setAiState:
		(aiState: State['aiState']): Action<State> =>
		({ setState }) => {
			setState({ aiState });
		},
	setIsCreatedWithAi:
		(isCreatedWithAi: State['isCreatedWithAi']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedWithAi });
		},
	setIsCreatedFromActionItem:
		(isCreatedFromActionItems: State['isCreatedFromActionItems']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedFromActionItems });
		},
	setInsertStorageFragmentRequestPayload:
		(
			insertStorageFragmentRequestPayload: State['insertStorageFragmentRequestPayload'],
		): Action<State> =>
		({ setState }) => {
			setState({ insertStorageFragmentRequestPayload });
		},
	setInsertMultipleStorageFragmentsRequestPayload:
		(
			insertMultipleStorageFragmentsRequestPayload: State['insertMultipleStorageFragmentsRequestPayload'],
		): Action<State> =>
		({ setState }) => {
			setState({ insertMultipleStorageFragmentsRequestPayload });
		},
	setInsertStorageColumnTableRequestPayload:
		(
			insertStorageColumnTableRequestPayload: State['insertStorageColumnTableRequestPayload'],
		): Action<State> =>
		({ setState }) => {
			setState({ insertStorageColumnTableRequestPayload });
		},
	setAiAnalyticsContext:
		(key: string, value: number): Action<State> =>
		({ setState, getState }) => {
			setState({
				aiAnalyticsContext: {
					...getState()['aiAnalyticsContext'],
					[key]: value,
				},
			});
		},
	setFirstAttempt:
		(firstAttempt: State['firstAttempt']): Action<State> =>
		({ setState }) => {
			setState({ firstAttempt });
		},
	setTotalRetries:
		(): Action<State> =>
		({ setState, getState }) => {
			const currentTotalRetries = getState().totalRetries;
			setState({ totalRetries: currentTotalRetries + 1 });
		},
	setCurrentSiteSelection:
		(currentSiteSelection: State['currentSiteSelection']): Action<State> =>
		({ setState }) => {
			setState({ currentSiteSelection });
		},
	setCurrentProjectSelection:
		(currentProjectSelection: State['currentProjectSelection']): Action<State> =>
		({ setState }) => {
			setState({ currentProjectSelection });
		},
	setAiIssueCreationEnablement:
		(aiIssueCreationEnablement: State['aiIssueCreationEnablement']): Action<State> =>
		({ setState }) => {
			setState({ aiIssueCreationEnablement });
		},
	setIsCreateManuallyPressed:
		(isCreateManuallyPressed: State['isCreateManuallyPressed']): Action<State> =>
		({ setState }) => {
			setState({ isCreateManuallyPressed });
		},
	setIsGicIframeReady:
		(isGicIframeReady: State['isGicIframeReady']): Action<State> =>
		({ setState }) => {
			setState({ isGicIframeReady });
		},
	setPageMode:
		(pageMode: State['pageMode']): Action<State> =>
		({ setState }) => {
			setState({ pageMode });
		},
	setHasEditPermission:
		(hasEditPermission: State['hasEditPermission']): Action<State> =>
		({ setState }) => {
			setState({ hasEditPermission });
		},
	setEditorAPI:
		(editorAPI: State['editorAPI']): Action<State> =>
		({ setState }) => {
			setState({ editorAPI });
		},
};

const Store = createStore({
	initialState: {
		...initialIssueCreateSidePanelState,
	},
	actions: issueCreateSidePanelActions,
	name: 'issueCreateSidePanelContext',
});

export const IssueCreateSidePanelContainer = createContainer(Store);

export const useIssueCreateSidePanelContext = createHook(Store);

export const useIssueCreateSidePanelActions = createActionsHook(Store);

export const useAiIssueCreationEnablementState = createStateHook(Store, {
	selector: (state) => state.aiIssueCreationEnablement,
});
